import { useEffect, useMemo, useState } from "react";
import type { UiCommandBusV2, UiSnapshotV2 } from "@/ui/runtime/contracts/manager-ui-v2";
import { Avatar, AvatarFallback, AvatarImage } from "@/ui/foundation/primitives/avatar";
import { Badge } from "@/ui/foundation/primitives/badge";
import { Button } from "@/ui/foundation/primitives/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/foundation/primitives/card";
import { DataList } from "@/ui/components/common/data-list";
import { AttributeScoreBar } from "@/ui/components/common/attribute-score-bar";
import { CheckCircle2Icon, FilterIcon } from "lucide-react";
import { avatarDataUri, initials } from "@/ui/components/manager/manager-ui-helpers";
import { ManagerSecondaryPane } from "@/ui/components/manager/manager-layout";
import { Input } from "@/ui/foundation/primitives/input";

interface InboxScreenState {
    selectedMessageId: string | null;
    inboxSearch: string;
    inboxUnresolvedOnly: boolean;
    inboxResolvedActions: Record<string, string>;
}

interface UseInboxScreenStateResult {
    state: InboxScreenState;
    selectedInbox: UiSnapshotV2["routes"]["inbox"]["items"][number] | undefined;
    filteredInboxItems: UiSnapshotV2["routes"]["inbox"]["items"];
    hasArchivedOlderMessages: boolean;
    setInboxSearch(value: string): void;
    toggleUnresolvedOnly(): void;
    selectInboxMessage(id: string): void;
    setResolvedAction(messageId: string, actionText: string): void;
}

const INBOX_LIST_WINDOW_DAYS = 30;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

function parseInboxDate(value: string): number | null {
    const raw = String(value ?? "").trim();
    if (!raw || raw === "--") {
        return null;
    }
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.getTime();
    }
    const withMiddayUtc = new Date(`${raw}T12:00:00.000Z`);
    if (!Number.isNaN(withMiddayUtc.getTime())) {
        return withMiddayUtc.getTime();
    }
    return null;
}

export function useInboxScreenState(snapshot: UiSnapshotV2): UseInboxScreenStateResult {
    const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
    const [inboxSearch, setInboxSearch] = useState<string>("");
    const [inboxUnresolvedOnly, setInboxUnresolvedOnly] = useState<boolean>(false);
    const [inboxResolvedActions, setInboxResolvedActions] = useState<Record<string, string>>({});

    const searchFilteredInboxItems = useMemo(() => {
        const query = inboxSearch.trim().toLowerCase();
        return snapshot.routes.inbox.items.filter((item) => {
            if (inboxUnresolvedOnly && item.status !== "unread" && item.status !== "read") {
                return false;
            }
            if (!query) {
                return true;
            }
            return `${item.sender} ${item.subject} ${item.preview}`.toLowerCase().includes(query);
        });
    }, [inboxSearch, inboxUnresolvedOnly, snapshot.routes.inbox.items]);

    const { filteredInboxItems, hasArchivedOlderMessages } = useMemo(() => {
        if (searchFilteredInboxItems.length <= 1) {
            return {
                filteredInboxItems: searchFilteredInboxItems,
                hasArchivedOlderMessages: false
            };
        }
        const newestTimestamp = parseInboxDate(searchFilteredInboxItems[0].date);
        if (newestTimestamp === null) {
            return {
                filteredInboxItems: searchFilteredInboxItems,
                hasArchivedOlderMessages: false
            };
        }
        const earliestAllowedTimestamp = newestTimestamp - (INBOX_LIST_WINDOW_DAYS * DAY_IN_MS);
        const monthLimitedItems = searchFilteredInboxItems.filter((item) => {
            const timestamp = parseInboxDate(item.date);
            if (timestamp === null) {
                return true;
            }
            return timestamp >= earliestAllowedTimestamp;
        });
        return {
            filteredInboxItems: monthLimitedItems,
            hasArchivedOlderMessages: monthLimitedItems.length < searchFilteredInboxItems.length
        };
    }, [searchFilteredInboxItems]);

    useEffect(() => {
        if (selectedMessageId && !filteredInboxItems.some((item) => item.id === selectedMessageId)) {
            setSelectedMessageId(null);
        }
    }, [selectedMessageId, filteredInboxItems]);

    const selectedInbox = useMemo(
        () => filteredInboxItems.find((item) => item.id === selectedMessageId) ?? filteredInboxItems[0],
        [filteredInboxItems, selectedMessageId]
    );

    return {
        state: { selectedMessageId, inboxSearch, inboxUnresolvedOnly, inboxResolvedActions },
        selectedInbox,
        filteredInboxItems,
        hasArchivedOlderMessages,
        setInboxSearch,
        toggleUnresolvedOnly: () => setInboxUnresolvedOnly((value) => !value),
        selectInboxMessage: setSelectedMessageId,
        setResolvedAction: (messageId: string, actionText: string) => {
            setInboxResolvedActions((current) => ({ ...current, [messageId]: actionText }));
        },
    };
}

interface InboxScreenSecondaryProps {
    state: UseInboxScreenStateResult;
    onOpenInboxMessage(messageId: string): void;
}

export function InboxScreenSecondary({ state, onOpenInboxMessage }: InboxScreenSecondaryProps) {
    return (
        <ManagerSecondaryPane
            title="Inbox"
            headerAction={(
                <Button
                    size="icon"
                    variant={state.state.inboxUnresolvedOnly ? "secondary" : "ghost"}
                    className="h-7 w-7"
                    onClick={state.toggleUnresolvedOnly}
                    aria-label="Toggle unresolved filter"
                    title={state.state.inboxUnresolvedOnly ? "Showing unresolved only" : "Show unresolved only"}
                >
                    <FilterIcon className="size-3.5" />
                </Button>
            )}
            search={{
                value: state.state.inboxSearch,
                onChange: state.setInboxSearch,
            }}
        >
            <div>
                <DataList
                    items={state.filteredInboxItems}
                    getKey={(mail) => mail.id}
                    selectedKey={state.selectedInbox?.id ?? null}
                    onSelect={(mail) => {
                        state.selectInboxMessage(mail.id);
                        onOpenInboxMessage(mail.id);
                    }}
                    itemClassName="flex flex-col items-start gap-2"
                    renderItem={(mail) => (
                        <>
                            <div className="flex w-full items-start gap-2">
                                <Avatar className="size-8">
                                    <AvatarImage alt={mail.sender} src={avatarDataUri(mail.avatarSvg)} />
                                    <AvatarFallback className="text-[10px]">{initials(mail.sender)}</AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-medium">{mail.sender}</div>
                                    <div className="text-xs text-muted-foreground">{mail.date}</div>
                                </div>
                                <div className="ml-auto flex shrink-0 items-center gap-1">
                                    {!mail.read ? <Badge variant="destructive">New</Badge> : null}
                                    {(mail.status === "unread" || mail.status === "read") ? (
                                        <Badge variant="outline" className="text-[10px]">Unresolved</Badge>
                                    ) : null}
                                </div>
                            </div>
                            <div className="w-full truncate text-sm">{mail.subject}</div>
                            <p className="w-full text-xs text-muted-foreground">{mail.preview}</p>
                        </>
                    )}
                />
                {state.hasArchivedOlderMessages ? (
                    <div className="px-4 py-3 text-center text-xs text-muted-foreground">older messages have been archived</div>
                ) : null}
            </div>
        </ManagerSecondaryPane>
    );
}

interface InboxScreenMainProps {
    commands: UiCommandBusV2;
    state: UseInboxScreenStateResult;
}

export function InboxScreenMain({ commands, state }: InboxScreenMainProps) {
    const [actionInputs, setActionInputs] = useState<Record<string, Record<string, string>>>({});
    const [activeActionOptionId, setActiveActionOptionId] = useState<string | null>(null);
    const selectedInbox = state.selectedInbox;
    useEffect(() => {
        setActiveActionOptionId(null);
    }, [selectedInbox?.id]);
    if (!selectedInbox) {
        return null;
    }
    const scoutReport = selectedInbox.transferScoutReport ?? null;
    const transferMarketEvent = selectedInbox.transferMarketEvent ?? null;
    const transferFocusTargetId = selectedInbox.transferFocusTargetId ?? null;
    const transferSummary = scoutReport ? null : (selectedInbox.transferPlayerSummary ?? null);
    const focusPlayerContext = selectedInbox.focusPlayerContext ?? null;
    const contextSections = selectedInbox.contextSections ?? [];
    const showActionRecorded = !scoutReport && (
        selectedInbox.status === "resolved"
        || selectedInbox.status === "expired"
        || Boolean(state.state.inboxResolvedActions[selectedInbox.id])
    );
    const reportAttributes = scoutReport
        ? ([
            ["pace", "Pace"],
            ["ballControl", "Ball Control"],
            ["passing", "Passing"],
            ["tackling", "Tackling"],
            ["endurance", "Endurance"],
            ["positioning", "Positioning"],
            ["shotPower", "Shot Power"],
            ["reactions", "Reactions"],
            ["composure", "Composure"],
            ["goalkeeping", "Goalkeeping"],
        ] as const)
        : [];
    const summaryBars = transferSummary
        ? ([
            ["pace", "Pace"],
            ["passing", "Passing"],
            ["tackling", "Tackling"],
            ["composure", "Composure"],
        ] as const)
        : [];
    const actionOptions = selectedInbox.options ?? [];
    const activeActionOption = actionOptions.find((option) => option.id === activeActionOptionId && option.actionForm) ?? null;

    const resolveOption = (
        optionId: string,
        optionText: string,
        actionForm?: NonNullable<NonNullable<(typeof actionOptions)[number]['actionForm']>>
    ) => {
        const key = `${selectedInbox.id}:${optionId}`;
        const payload: { bidAmount?: number; wageOffer?: number; listingPrice?: number } = {};
        if (actionForm) {
            for (const field of actionForm.fields) {
                const raw = actionInputs[key]?.[field.id] ?? String(field.defaultValue);
                const numeric = Number(raw);
                if (Number.isFinite(numeric)) {
                    if (field.id === "bidAmount") {
                        payload.bidAmount = numeric;
                    }
                    if (field.id === "wageOffer") {
                        payload.wageOffer = numeric;
                    }
                    if (field.id === "listingPrice") {
                        payload.listingPrice = numeric;
                    }
                }
            }
        }
        state.setResolvedAction(selectedInbox.id, optionText);
        void commands.inboxResolve(
            selectedInbox.id,
            optionId,
            actionForm ? payload : undefined
        );
    };

    return (
        <Card>
            <CardHeader>
                <CardDescription>{selectedInbox.sender} • {selectedInbox.date}</CardDescription>
                <CardTitle>{selectedInbox.subject}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
                <p className="text-sm text-muted-foreground">{selectedInbox.prompt ?? selectedInbox.preview}</p>
                {focusPlayerContext ? (
                    <div className="grid gap-3 rounded-md border p-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="text-sm font-medium">{focusPlayerContext.name} · {focusPlayerContext.role}</div>
                            <Badge variant="outline">{focusPlayerContext.selectionStatus}</Badge>
                            <Badge variant={focusPlayerContext.transferListed ? "destructive" : "secondary"}>
                                {focusPlayerContext.transferListed ? "Transfer listed" : "Not listed"}
                            </Badge>
                        </div>
                        <div className="grid gap-2 md:grid-cols-3">
                            <div className="rounded-md border bg-muted/40 px-2 py-1.5 text-xs">
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Role Rank</div>
                                <div className="font-medium">#{focusPlayerContext.roleRank}/{focusPlayerContext.roleCount} · OVR {focusPlayerContext.roleOverall}</div>
                            </div>
                            <div className="rounded-md border bg-muted/40 px-2 py-1.5 text-xs">
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Appearances</div>
                                <div className="font-medium">Starts {focusPlayerContext.stats.starts} · Bench {focusPlayerContext.stats.benchAppearances}</div>
                            </div>
                            <div className="rounded-md border bg-muted/40 px-2 py-1.5 text-xs">
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Output</div>
                                <div className="font-medium">Goals {focusPlayerContext.stats.goals} · Assists {focusPlayerContext.stats.assists}</div>
                            </div>
                            <div className="rounded-md border bg-muted/40 px-2 py-1.5 text-xs md:col-span-3">
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Selection Recency</div>
                                <div className="font-medium">
                                    {focusPlayerContext.stats.lastSelectedMatchday !== null
                                        ? `Last selected: matchday ${focusPlayerContext.stats.lastSelectedMatchday}`
                                        : "Last selected: not yet this season"}
                                </div>
                            </div>
                        </div>
                        <div className="grid gap-2 md:grid-cols-2">
                            {focusPlayerContext.keyAttributes.map((attribute) => (
                                <AttributeScoreBar
                                    key={`inbox-focus-player-attr-${attribute.key}`}
                                    label={attribute.label}
                                    value={Math.round(Number(attribute.value))}
                                />
                            ))}
                        </div>
                    </div>
                ) : null}
                {!focusPlayerContext && contextSections.length > 0 ? (
                    <div className="grid gap-2">
                        {contextSections.map((section, index) => (
                            <div key={`inbox-context-section-${index}`} className="rounded-md border p-3">
                                <div className="text-sm font-medium">{section.title}</div>
                                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                    {section.lines.map((line, lineIndex) => (
                                        <Badge
                                            key={`inbox-context-line-${index}-${lineIndex}`}
                                            variant="outline"
                                            className="max-w-full whitespace-normal text-left leading-relaxed"
                                        >
                                            {line}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : null}
                {scoutReport ? (
                    <div className="grid gap-3 rounded-md border p-3">
                        <div className="text-sm font-medium">Scout confidence {Math.round(scoutReport.scoutConfidence)}</div>
                        <p className="text-sm text-muted-foreground">{scoutReport.opinion}</p>
                        <div className="grid gap-2 md:grid-cols-2">
                            {reportAttributes.map(([key, label]) => (
                                <AttributeScoreBar
                                    key={`inbox-scout-${key}`}
                                    label={label}
                                    value={Math.round(Number(scoutReport.attributes[key]))}
                                    uncertainty={Math.max(0, Math.round(Number(scoutReport.uncertaintyByAttribute[key])))}
                                />
                            ))}
                        </div>
                    </div>
                ) : null}
                {transferMarketEvent ? (
                    <div className="grid gap-2 rounded-md border p-3">
                        <div className="text-sm font-medium">Transfer Market Event</div>
                        <div className="text-xs text-muted-foreground">
                            {transferMarketEvent.playerName}: {transferMarketEvent.priorContext}
                        </div>
                        <div className="text-xs">
                            {transferMarketEvent.fromClubName ?? "Source club"} {"->"} {transferMarketEvent.toClubName ?? "Destination undisclosed"}
                        </div>
                        <div className="text-xs">
                            {transferMarketEvent.fee !== null ? `Fee £${Math.round(transferMarketEvent.fee).toLocaleString()}` : "Fee undisclosed"}
                        </div>
                        <div className="text-xs text-muted-foreground">{transferMarketEvent.impact}</div>
                    </div>
                ) : null}
                {transferSummary ? (
                    <div className="grid gap-2 rounded-md border p-3">
                        <div className="text-sm font-medium">Position: {transferSummary.position}</div>
                        <div className="grid gap-2 md:grid-cols-2">
                            {summaryBars.map(([key, label]) => (
                                <AttributeScoreBar
                                    key={`inbox-summary-${key}`}
                                    label={label}
                                    value={Math.round(Number(transferSummary.attributes[key]))}
                                />
                            ))}
                        </div>
                    </div>
                ) : null}
                {transferFocusTargetId ? (
                    <Button
                        onClick={() => {
                            void (async () => {
                                await commands.transferFocusTarget(transferFocusTargetId);
                                await commands.navigate('/manager/transfers');
                            })();
                        }}
                    >
                        Open In Transfers
                    </Button>
                ) : null}
                {showActionRecorded ? (
                    <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm">
                        <div className="flex items-center gap-2 font-medium text-emerald-700 dark:text-emerald-300">
                            <CheckCircle2Icon className="size-4" />
                            Action recorded
                        </div>
                        <div className="mt-1 text-emerald-700/85 dark:text-emerald-300/85">
                            {state.state.inboxResolvedActions[selectedInbox.id] ?? "This item has been resolved."}
                        </div>
                    </div>
                ) : selectedInbox.options && selectedInbox.options.length > 0 ? (
                    <div className="grid gap-2">
                        <div className="flex flex-wrap gap-2">
                            {selectedInbox.options.map((option) => (
                                <Button
                                    key={option.id}
                                    variant={activeActionOptionId === option.id ? "default" : "outline"}
                                    onClick={() => {
                                        if (!option.actionForm) {
                                            resolveOption(option.id, option.text);
                                            return;
                                        }
                                        setActiveActionOptionId(option.id);
                                    }}
                                >
                                    {option.text}
                                </Button>
                            ))}
                        </div>
                        {activeActionOption?.actionForm ? (
                            <div className="grid gap-2">
                                <div className="text-xs font-medium text-muted-foreground">{activeActionOption.actionForm.title}</div>
                                {activeActionOption.actionForm.fields.map((field) => {
                                    const key = `${selectedInbox.id}:${activeActionOption.id}`;
                                    const value = actionInputs[key]?.[field.id] ?? String(field.defaultValue);
                                    return (
                                        <div key={`${activeActionOption.id}-${field.id}`} className="grid gap-1">
                                            <label className="text-xs text-muted-foreground">{field.label}</label>
                                            <Input
                                                inputMode="numeric"
                                                value={value}
                                                onChange={(event) => {
                                                    const next = event.target.value.replace(/[^0-9]/g, "");
                                                    setActionInputs((current) => ({
                                                        ...current,
                                                        [key]: {
                                                            ...(current[key] ?? {}),
                                                            [field.id]: next
                                                        }
                                                    }));
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                                <div className="flex gap-2">
                                    <Button
                                        onClick={() => resolveOption(activeActionOption.id, activeActionOption.text, activeActionOption.actionForm ?? undefined)}
                                    >
                                        {activeActionOption.actionForm.submitLabel}
                                    </Button>
                                    <Button variant="outline" onClick={() => setActiveActionOptionId(null)}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <Button variant="outline" onClick={() => void commands.inboxOpen(selectedInbox.id)}>Mark Read</Button>
                )}
            </CardContent>
        </Card>
    );
}
